import { useState } from 'react'
import { X, Loader2, ShieldAlert, AlertTriangle, PackageOpen } from 'lucide-react'
import { useVoidReceipt } from '@/api/receipts'
import { useCloseEcomReceipt } from '@/hooks'
import { usePOSStore } from '@/stores/posStore'
import { useUIStore } from '@/stores/uiStore'

export function VoidReceiptModal() {
  const { closeModal, showToast } = useUIStore()
  const { currentReceiptId, loadedEcomMeta, clearReceipt } = usePOSStore()
  const [error, setError] = useState('')

  const voidReceipt = useVoidReceipt()
  const closeEcom = useCloseEcomReceipt()

  // A loaded ecom/delivery order is CLOSED, not voided: the clerk may just be
  // viewing it (no customer present), so we revert it to its pre-load status
  // (normally Hold) and clear the register rather than cancelling it. Real
  // cancellation stays on the OrderDetailModal "Cancel Order" button.
  const isEcomClose = !!currentReceiptId && !!loadedEcomMeta

  const handleClose = async () => {
    setError('')
    try {
      await closeEcom.close()
      closeModal()
      showToast({
        type: 'success',
        title: 'Order Closed',
        message: loadedEcomMeta?.isDelivery
          ? 'Delivery order returned to the list. It was not cancelled.'
          : 'Order returned to the list. It was not cancelled.',
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not close order.')
    }
  }

  // No backend receipt — just clear the local cart.
  if (!currentReceiptId) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
        <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col animate-scale-in">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <AlertTriangle size={20} className="text-amber-500" />
              <h2 className="text-lg font-semibold text-gray-900">Clear Cart?</h2>
            </div>
            <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-500 mb-6">
              This will remove all items from the cart. No receipt has been submitted yet.
            </p>
            <div className="flex gap-3">
              <button
                onClick={closeModal}
                className="flex-1 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => { clearReceipt(); closeModal(); showToast({ type: 'success', title: 'Cart Cleared', message: 'Transaction has been cancelled.' }) }}
                className="flex-1 py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-xl transition-colors cursor-pointer"
              >
                Clear Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Backend receipt exists — confirm and call the v2 void endpoint.
  const handleVoid = async () => {
    setError('')
    try {
      await voidReceipt.mutateAsync({ receiptId: currentReceiptId })
      clearReceipt()
      closeModal()
      showToast({ type: 'success', title: 'Receipt Voided', message: 'Transaction has been cancelled.' })
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { error?: string; code?: string } } }
      const code = axiosErr?.response?.data?.code
      const msg = axiosErr?.response?.data?.error || (err instanceof Error ? err.message : 'Void failed.')
      const status = axiosErr?.response?.status
      const errMsg = msg.toLowerCase()

      // Terminal — receipt is already in a state the backend won't let
      // us mutate (paid, locked, voided, deleted, mid-payment). Same
      // policy as LeftSidebar.handleVoid: detach locally so the cashier
      // isn't wedged against a dead receipt that every retry will 500
      // against. clearReceipt() is local-only — safe to run regardless
      // of BE state.
      const isTerminal =
        status === 409
        || status === 500
        || code === 'cannot_cancel_paid'
        || code === 'already_terminal'
        || code === 'payment_in_progress'
        || errMsg.includes('is locked')
        || errMsg.includes('cannot be modified')
        || errMsg.includes('already paid')
        || errMsg.includes('with payments')
        || errMsg.includes('cancel any payments')

      if (isTerminal) {
        clearReceipt()
        closeModal()
        showToast({
          type: 'warning',
          title: 'Receipt Already Closed',
          message:
            code === 'cannot_cancel_paid' || errMsg.includes('already paid') || errMsg.includes('with payments')
              ? "Paid receipts can't be voided. Cart cleared — use Refund/Return for paid orders."
              : 'Receipt is closed on the backend. Cart cleared so you can start fresh.',
        })
        return
      }

      setError(msg)
    }
  }

  // Ecom/delivery order loaded into the register → Close (revert + clear),
  // never cancel.
  if (isEcomClose) {
    const busy = closeEcom.isPending
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
        <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col animate-scale-in">
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <PackageOpen size={20} className="text-blue-500" />
              <h2 className="text-lg font-semibold text-gray-900">Close Order</h2>
            </div>
            <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
              <X size={20} className="text-gray-500" />
            </button>
          </div>
          <div className="p-6">
            <p className="text-sm text-gray-500 mb-6">
              This closes the order and returns it to the{' '}
              {loadedEcomMeta?.isDelivery ? 'Deliveries' : 'Orders'} list
              unchanged. The order is <span className="font-semibold">not cancelled</span> —
              to cancel it, use “Cancel Order” from the order details.
            </p>
            {error && <p className="mb-4 text-sm text-red-500">{error}</p>}
            <div className="flex gap-3">
              <button
                onClick={closeModal}
                disabled={busy}
                className="flex-1 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer disabled:opacity-50"
              >
                Keep Open
              </button>
              <button
                onClick={handleClose}
                disabled={busy}
                className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                Close Order
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center animate-fade-in">
      <div className="absolute inset-0 bg-black/50" onClick={closeModal} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden flex flex-col animate-scale-in">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ShieldAlert size={20} className="text-red-500" />
            <h2 className="text-lg font-semibold text-gray-900">Void Receipt</h2>
          </div>
          <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <p className="text-sm text-gray-500 mb-6">
            This will void the current receipt. This action cannot be undone.
          </p>

          {error && <p className="mb-4 text-sm text-red-500">{error}</p>}

          <div className="flex gap-3">
            <button
              onClick={closeModal}
              disabled={voidReceipt.isPending}
              className="flex-1 py-3 border border-gray-200 hover:bg-gray-50 text-gray-700 font-medium rounded-xl transition-colors cursor-pointer disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleVoid}
              disabled={voidReceipt.isPending}
              className="flex-1 py-3 bg-red-500 hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors cursor-pointer flex items-center justify-center gap-2"
            >
              {voidReceipt.isPending && <Loader2 size={16} className="animate-spin" />}
              Void Receipt
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
